import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import {
  Search as SearchIcon,
  GraduationCap,
  User,
  Ticket,
  Users,
  MessageSquare,
  ClipboardList,
  School,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useBranchLabels from '../hooks/useBranchLabels';
import { isCrmRole } from '../utils/crmPermissions';

const RESULT_LIMIT = 10;

function includesTerm(value, term) {
  return value != null && String(value).toLowerCase().includes(term);
}

function ResultSection({ icon: Icon, title, items, renderItem }) {
  if (!items.length) return null;
  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-lg text-foreground flex items-center gap-2">
          <Icon className="h-5 w-5" />
          {title} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.slice(0, RESULT_LIMIT).map(renderItem)}
        {items.length > RESULT_LIMIT && (
          <p className="text-sm text-muted-foreground">+ {items.length - RESULT_LIMIT} more</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function Search() {
  const { user, isSchoolObserver: observerMode } = useAuth();
  const { formatLabel } = useBranchLabels();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get('q') || '';
  const [query, setQuery] = useState(q);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);

  const isCrmSearch = isCrmRole(user) && !observerMode;
  const searchPlaceholder = isCrmSearch
    ? 'Search tickets, employees, feedback, enquiries, schools...'
    : 'Search students, teachers...';

  useEffect(() => {
    setQuery(q);
  }, [q]);

  const clearSchoolResults = () => {
    setStudents([]);
    setTeachers([]);
  };

  const clearCrmResults = () => {
    setTickets([]);
    setEmployees([]);
    setFeedback([]);
    setInquiries([]);
    setSchools([]);
  };

  const runSchoolSearch = (term) => {
    const schoolId = user?.school_id;
    const academicYearId = schoolId ? localStorage.getItem(`selected_academic_year_id_${schoolId}`) : null;
    const params = {
      ...(schoolId ? { school_id: schoolId } : {}),
      ...(academicYearId ? { academic_year_id: academicYearId } : {}),
    };
    return Promise.all([
      api.get('students', { params }).catch(() => ({ data: { data: [] } })),
      api.get('teachers', { params }).catch(() => ({ data: { data: [] } })),
    ]).then(([sRes, tRes]) => {
      const sList = sRes.data.data || [];
      const tList = tRes.data.data || [];
      setStudents(
        sList.filter(
          (s) =>
            includesTerm(s.first_name, term) ||
            includesTerm(s.last_name, term) ||
            includesTerm(s.admission_no, term) ||
            includesTerm(s.email, term)
        )
      );
      setTeachers(
        tList.filter(
          (t) =>
            includesTerm(t.first_name, term) ||
            includesTerm(t.last_name, term) ||
            includesTerm(t.employee_id, term) ||
            includesTerm(t.email, term)
        )
      );
    });
  };

  const runCrmSearch = (term) => {
    return Promise.all([
      api.get('crm/tickets', { params: { search: term, limit: 50 } }).catch(() => ({ data: { data: { items: [] } } })),
      api.get('crm/employees').catch(() => ({ data: { data: { items: [] } } })),
      api.get('feedback', { params: { q: term, limit: 50 } }).catch(() => ({ data: { data: { items: [] } } })),
      api.get('crm/inquiries').catch(() => ({ data: { data: { items: [] } } })),
      api.get('schools').catch(() => ({ data: { data: [] } })),
    ]).then(([ticketRes, empRes, feedbackRes, inquiryRes, schoolRes]) => {
      const ticketItems = ticketRes.data?.data?.items || [];
      setTickets(
        ticketItems.filter(
          (t) => includesTerm(t.ticket_code, term) || includesTerm(t.title, term) || includesTerm(t.description, term)
        )
      );

      const empItems = empRes.data?.data?.items || [];
      setEmployees(
        empItems.filter(
          (e) =>
            includesTerm(e.first_name, term) ||
            includesTerm(e.last_name, term) ||
            includesTerm(`${e.first_name || ''} ${e.last_name || ''}`.trim(), term) ||
            includesTerm(e.employee_code, term) ||
            includesTerm(e.email, term)
        )
      );

      const feedbackItems = feedbackRes.data?.data?.items || [];
      setFeedback(
        feedbackItems.filter(
          (f) =>
            includesTerm(f.title, term) ||
            includesTerm(f.feedback_code, term) ||
            includesTerm(f.school_name, term) ||
            includesTerm(f.description, term)
        )
      );

      const inquiryItems = inquiryRes.data?.data?.items || [];
      setInquiries(
        inquiryItems.filter(
          (i) =>
            includesTerm(i.school_name, term) ||
            includesTerm(i.email, term) ||
            includesTerm(i.principal_name, term) ||
            includesTerm(i.contact_number, term)
        )
      );

      const schoolList = schoolRes.data?.data?.items || schoolRes.data?.data || [];
      const list = Array.isArray(schoolList) ? schoolList : [];
      setSchools(
        list.filter(
          (s) =>
            includesTerm(s.name, term) ||
            includesTerm(s.code, term) ||
            includesTerm(s.email, term) ||
            includesTerm(s.phone, term)
        )
      );
    });
  };

  useEffect(() => {
    if (!query.trim()) {
      clearSchoolResults();
      clearCrmResults();
      return;
    }
    setLoading(true);
    const term = query.trim().toLowerCase();
    if (isCrmSearch) {
      clearSchoolResults();
      runCrmSearch(term).finally(() => setLoading(false));
    } else {
      clearCrmResults();
      runSchoolSearch(term).finally(() => setLoading(false));
    }
  }, [query, user?.school_id, isCrmSearch]);

  useEffect(() => {
    if (isCrmSearch) return undefined;
    const handler = () => {
      if (!query.trim()) return;
      setStudents([]);
      setTeachers([]);
      setLoading(true);
      const term = query.trim().toLowerCase();
      runSchoolSearch(term).finally(() => setLoading(false));
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [query, user?.school_id, isCrmSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = (e.target.query && e.target.query.value) || query;
    if (value.trim()) navigate(`/search?q=${encodeURIComponent(value.trim())}`);
  };

  const crmHasResults =
    tickets.length > 0 ||
    employees.length > 0 ||
    feedback.length > 0 ||
    inquiries.length > 0 ||
    schools.length > 0;
  const schoolHasResults = students.length > 0 || teachers.length > 0;
  const hasResults = isCrmSearch ? crmHasResults : schoolHasResults;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground">Search</h1>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            name="query"
            type="search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-11 border-border text-foreground"
          />
        </div>
      </form>
      {loading && <p className="text-muted-foreground">Searching...</p>}
      {!loading && query.trim() && (
        <>
          {isCrmSearch ? (
            <>
              <ResultSection
                icon={Ticket}
                title="Tickets"
                items={tickets}
                renderItem={(t) => (
                  <Link
                    key={t.id}
                    to={`/crm/tickets/${t.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {t.title || 'Untitled ticket'}
                    {t.ticket_code && <span className="text-muted-foreground ml-2">({t.ticket_code})</span>}
                  </Link>
                )}
              />
              <ResultSection
                icon={Users}
                title="Employees"
                items={employees}
                renderItem={(e) => (
                  <Link
                    key={e.id}
                    to={`/crm/employees/${e.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {`${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Employee'}
                    {e.employee_code && <span className="text-muted-foreground ml-2">({e.employee_code})</span>}
                  </Link>
                )}
              />
              <ResultSection
                icon={MessageSquare}
                title="Feedback"
                items={feedback}
                renderItem={(f) => (
                  <Link
                    key={f.id}
                    to={`/crm/feedback/${f.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {f.title || 'Feedback'}
                    {f.feedback_code && <span className="text-muted-foreground ml-2">({f.feedback_code})</span>}
                    {f.school_name && <span className="text-muted-foreground ml-2">— {f.school_name}</span>}
                  </Link>
                )}
              />
              <ResultSection
                icon={ClipboardList}
                title="Enquiries"
                items={inquiries}
                renderItem={(i) => (
                  <Link
                    key={i.id}
                    to={`/crm/inquiries/${i.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {i.school_name || 'Enquiry'}
                    {i.principal_name && <span className="text-muted-foreground ml-2">— {i.principal_name}</span>}
                    {i.email && <span className="text-muted-foreground ml-2">({i.email})</span>}
                  </Link>
                )}
              />
              <ResultSection
                icon={School}
                title="Schools"
                items={schools}
                renderItem={(s) => (
                  <Link
                    key={s.id}
                    to={`/schools/${s.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {s.name || 'School'}
                    {s.code && <span className="text-muted-foreground ml-2">({s.code})</span>}
                  </Link>
                )}
              />
            </>
          ) : (
            <>
              <ResultSection
                icon={GraduationCap}
                title="Students"
                items={students}
                renderItem={(s) => (
                  <Link
                    key={s.id}
                    to={`/students/${s.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {formatLabel(`${s.first_name} ${s.last_name}`.trim(), s)}
                    {s.admission_no && <span className="text-muted-foreground ml-2">({s.admission_no})</span>}
                  </Link>
                )}
              />
              <ResultSection
                icon={User}
                title="Teachers"
                items={teachers}
                renderItem={(t) => (
                  <Link
                    key={t.id}
                    to={`/hr/teachers/${t.id}`}
                    className="block p-3 rounded-lg bg-muted/50 hover:bg-muted text-foreground"
                  >
                    {formatLabel(`${t.first_name} ${t.last_name}`.trim(), t)}
                    {t.employee_id && <span className="text-muted-foreground ml-2">({t.employee_id})</span>}
                  </Link>
                )}
              />
            </>
          )}
          {!hasResults && (
            <p className="text-muted-foreground">No results for &quot;{query}&quot;</p>
          )}
        </>
      )}
    </div>
  );
}
